import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './utils/api';
import { errorMessage, renderable } from './utils/errorMessage';
// Phase 7 sign-off: profiles email, display_name, short_text.
import { MAX_LENGTHS } from './utils/inputValidation';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import OrgCreditPanel from './components/OrgCreditPanel';
import './UserManagement.css';

const ROLE_COLORS = { admin: '#3f51b5', tester: '#ff9800', user: '#78909c' };
const ROLE_LABELS = { admin: 'Org-Admin', tester: 'Sys-Admin', user: 'Viewer' };

export default function UserManagement() {
  const { isSiteAdmin } = useAuth();
  // The credit panel is bilingual (T11); the rest of this screen is
  // English-only and stays that way in this change.
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // { mode: 'invite' | 'edit' | 'credits', item? }
  const [form, setForm] = useState({ email: '', full_name: '', role: 'user' });
  const [tempPw, setTempPw] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [seats, setSeats] = useState(null); // { current_seats, max_seats, plan_tier }
  const [subscription, setSubscription] = useState(null); // Subscription details
  // ORG credit pool — the ONLY balance that pays for an analysis
  // (organizations.credits_remaining). Not users.credit_balance, which is a
  // legacy per-user counter that gates nothing on the analyze path.
  // /admin/credits/org-usage: { credits_remaining, credits_used, credit_limit,
  // period_start }. Only `credits_remaining` is read from it now -- see the
  // ONE SOURCE PER FIELD note below, and the panel's note on why the current
  // period's start cannot be turned into a renewal date.
  const [orgCredits, setOrgCredits] = useState(null);
  // ⚠ ONE SOURCE PER FIELD (review 2026-09-24). This screen used to take the
  // panel's DENOMINATOR from `/admin/credits/org-usage.credit_limit`, which is
  // `subscription_tiers.credit_limit` keyed on `organizations.subscription_tier`
  // (subscription_service.get_org_subscription), while Settings and the
  // analysis page took theirs from `membership_tiers.credit_limit` -- the
  // column `is_unlimited_org()` and the grant path actually read. The two
  // tables line up today only because migration 042 happened to set both, and
  // `organizations.subscription_tier` is written by `upgrade_subscription`
  // while the membership row is written independently. An org that bought Pro
  // and later moved to a Starter membership printed "180 / 600" here and
  // "180 / 200" in Settings: the cross-screen disagreement T11 exists to end,
  // one layer up from the one it fixed.
  //
  // So the allowance and the unlimited flag now come from GET /credits/balance's
  // `membership_tier`, which is ORG-scoped (`um.org_id = :org_id`, active and
  // unexpired only) and reads membership_tiers -- the same object the analysis
  // page already uses. `membership_tier === null` therefore means "this org has
  // no active plan membership", which is the one thing a bare
  // `credit_limit == null` could never tell us apart from "unlimited tier".
  const [orgTier, setOrgTier] = useState(null); // { name, display_name, credit_limit, is_unlimited }
  const [orgPoolBalance, setOrgPoolBalance] = useState(null); // organizations.credits_remaining
  const [orgPoolAnswered, setOrgPoolAnswered] = useState(false);
  // Per-period consumption and the plan term, from GET /profile/membership.
  // `org_credits.credits_used_this_period` is derived ORG-WIDE by
  // `consumed_in_current_period(org_id=...)` (api/profile.py), and the endpoint
  // is ungated (plain get_current_user) -- so the note that used to sit on
  // `usedThisPeriod={null}` here ("no endpoint returns the org's per-period
  // consumption") was simply wrong, and it made this screen print a dash for a
  // number Settings was already showing for the same org on the same day.
  const [orgUsedThisPeriod, setOrgUsedThisPeriod] = useState(null);
  const [planDates, setPlanDates] = useState(null); // { start_date, end_date }
  // Probes, not values: BOTH credit reads have to have finished before "we do
  // not know the balance" is a true statement rather than "we have not asked
  // yet". Without these the panel would accuse the network of an error for the
  // first few hundred milliseconds of every visit.
  const [orgUsageProbed, setOrgUsageProbed] = useState(false);
  const [orgPoolProbed, setOrgPoolProbed] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (roleFilter) params.append('role', roleFilter);
      const res = await apiFetch(`/api/v1/admin/users?${params}`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data.items || []);
        setTotal(data.total || 0);
      }
    } catch { setError('Failed to load users'); }
  }, [page, roleFilter]);

  const loadSeats = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/users/seats');
      if (res.ok) setSeats(await res.json());
    } catch { /* ignore */ }
  }, []);
  
  const loadOrgCredits = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/credits/org-usage');
      if (res.ok) setOrgCredits(await res.json());
    } catch { /* non-fatal — loadOrgPool below reads the same pool */ }
    finally { setOrgUsageProbed(true); }
  }, []);

  // The two ungated reads the shared panel needs, fetched as one unit so the
  // panel never renders half of a plan. Same pair, same order, same fields as
  // App.js's `loadBalance` -- that is the point: three screens, one set of
  // sources. See the ONE SOURCE PER FIELD note on the state above.
  const loadOrgPool = useCallback(async () => {
    try {
      const [balRes, memRes] = await Promise.all([
        apiFetch('/api/v1/credits/balance'),
        apiFetch('/api/v1/profile/membership'),
      ]);
      if (balRes.ok) {
        const data = await balRes.json();
        // ⚠ NORMALISE TO number|null, NEVER TO 0 (O3). `balance` is being
        // changed to return null for "no organizations row / unknown", and an
        // absent key would otherwise flow through as `undefined` and defeat
        // every `=== null` check downstream.
        setOrgPoolBalance(typeof data.balance === 'number' && Number.isFinite(data.balance)
          ? data.balance
          : null);
        setOrgTier(data.membership_tier || null);
        // Set ONLY on a 200: it means "this route answered", so its null is a
        // FACT ("we do not know the pool") rather than "we never asked". The
        // fallback order below turns on that distinction.
        setOrgPoolAnswered(true);
      }
      if (memRes.ok) {
        const d = await memRes.json();
        setOrgUsedThisPeriod(
          typeof d?.org_credits?.credits_used_this_period === 'number'
            ? d.org_credits.credits_used_this_period
            : null,
        );
        // Only an ACTIVE membership has a period at all; a pending or expired
        // row renews nothing. USER-scoped, so it is null for an org admin who
        // did not personally buy the plan -- see the 갱신일 note at the panel.
        setPlanDates(d?.membership?.status === 'active'
          ? { start_date: d.membership.start_date || null, end_date: d.membership.end_date || null }
          : null);
      }
    } catch { /* the panel says so via unavailableReason */ }
    finally { setOrgPoolProbed(true); }
  }, []);

  const loadSubscription = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/subscription');
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
        
        // Update seats info from subscription
        if (data.tier_details) {
          const activeUserCount = users.filter(u => u.is_active).length;
          setSeats({
            current_seats: activeUserCount,
            max_seats: data.tier_details.user_limit || 999,
            plan_tier: data.tier_details.display_name,
          });
        }
      }
    } catch (err) {
      console.warn('Failed to load subscription:', err);
    }
  }, [users]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadSeats(); }, [loadSeats]);
  useEffect(() => { loadSubscription(); }, [loadSubscription]);
  useEffect(() => { loadOrgCredits(); }, [loadOrgCredits]);
  useEffect(() => { loadOrgPool(); }, [loadOrgPool]);

  const filteredUsers = search
    ? users.filter(u => u.full_name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()))
    : users;

  const handleInvite = async () => {
    setError('');
    setMessage('');
    
    // Check subscription user limit before invite
    if (subscription && subscription.tier_details) {
      const activeCount = users.filter(u => u.is_active).length;
      const userLimit = subscription.tier_details.user_limit;
      
      if (userLimit && activeCount >= userLimit) {
        setError(`User limit reached for ${subscription.subscription_tier} tier (${activeCount}/${userLimit}). Upgrade subscription to add more users.`);
        return;
      }
    }
    
    try {
      const res = await apiFetch('/api/v1/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify({ email: form.email, full_name: form.full_name, role: form.role }),
      });
      if (res.ok) {
        const data = await res.json();
        setModal(null);
        if (data.email_sent) {
          setMessage(`Invitation sent to ${form.email}`);
        } else if (data.temporary_password) {
          setTempPw(data.temporary_password);
        } else {
          setMessage('User invited');
        }
        loadUsers();
        loadSeats();
        loadSubscription();
      } else {
        const err = await res.json().catch(() => ({}));
        
        // Handle 403 user limit error from backend
        if (res.status === 403 && err.error === 'user_limit_reached') {
          setError(err.message || `User limit reached for your subscription tier. Please upgrade to add more users.`);
        } else {
          setError(errorMessage(err, 'Failed to invite user'));
        }
      }
    } catch { setError('Failed to invite user'); }
  };

  const handleUpdate = async () => {
    setError('');
    try {
      const res = await apiFetch(`/api/v1/admin/users/${modal.item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ full_name: form.full_name, email: form.email, role: form.role }),
      });
      if (res.ok) {
        setModal(null);
        setMessage('User updated');
        loadUsers();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(errorMessage(err, 'Failed to update'));
      }
    } catch { setError('Update failed'); }
  };

  const toggleActive = async (u) => {
    const action = u.is_active ? 'Revoke access for' : 'Reactivate';
    const extra = u.is_active ? ' They will be immediately logged out.' : '';
    if (!window.confirm(`${action} ${u.full_name}?${extra}`)) return;
    try {
      const res = u.is_active
        ? await apiFetch(`/api/v1/admin/users/${u.id}`, { method: 'DELETE' })
        : await apiFetch(`/api/v1/admin/users/${u.id}`, {
          method: 'PUT',
          body: JSON.stringify({ is_active: true }),
        });
      // apiFetch DOES NOT THROW ON 4xx/5xx — it returns the response for every
      // status except 401. Without this check the `catch` below only fired on a
      // network failure, so a 403 or 409 left the admin having confirmed
      // "They will be immediately logged out", seen no error, and the user kept
      // their access. A revoke that fails silently is worse than one that
      // errors: the admin believes the access is gone and stops watching.
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(
          (u.is_active ? 'Revoke failed' : 'Reactivate failed')
          + `: ${body.detail || body.error || `HTTP ${res.status}`}`
        );
        return;
      }
      setMessage(u.is_active
        ? `Access revoked for ${u.full_name}`
        : `${u.full_name} reactivated`);
      loadUsers();
      loadSeats();
    } catch { setError('Action failed'); }
  };

  const resetPassword = async (u) => {
    try {
      const res = await apiFetch(`/api/v1/admin/users/${u.id}/reset-password`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setTempPw(data.temporary_password);
      } else setError('Reset failed');
    } catch { setError('Reset failed'); }
  };

  // T9 (2026-09-24) — the per-user cap handler lived here and is gone.
  // Credits are an ORG pool shared by every member, so there is no per-user
  // ceiling to set. The control also never worked for the people who saw it:
  // its endpoint was site-admin gated, so an org admin pressing "Credits" on
  // their own team got "Site admin access required".

  const totalPages = Math.ceil(total / 20);

  // ORG CREDIT POOL — what this screen KNOWS, not how it looks. T11 moved the
  // rendering into components/OrgCreditPanel, shared with the Settings 구독 card
  // and the analysis page, so the three cannot drift apart again.
  //
  // O3 (2026-09-23) — `/admin/credits/org-usage` USED to be site-admin gated,
  // so for an ORG admin it 403'd, `orgCredits` stayed null, and the chip that
  // used to live here rendered `(orgCreditsRemaining ?? 0)` — printing a
  // confident "0 org credits" that was really a rejected request. The customer
  // read it as "my organisation has no credits".
  //
  // ⚠ THAT GATE HAS MOVED (T10, 2026-09-24). The READ is now on the in-org
  // `access:user_management` permission — it serves the caller's OWN org, taken
  // from their session with no parameter to aim it elsewhere, and the same
  // number is already ungated on GET /credits/balance. Only the org-GRANT WRITE
  // stayed on the site-admin flags, because that one mints spendable credits
  // and every self-signup holds `access:user_management`. The two gates
  // disagreeing is the fix, not a leftover: see the block at the top of
  // backend/app/api/credits.py before touching either. Do NOT "restore"
  // _site_admin on the read; test_credits_router_is_NOT_gated_and_that_is_deliberate
  // asserts the in-org gate by name.
  //
  // ⚠ THE FALLBACK WAS ALSO UNREACHABLE. The chip was wrapped in
  // `{orgCredits && (...)}`, so when the admin call 403'd nothing rendered at
  // all. The panel is gated on the resolved VALUE instead of on the admin
  // response.
  //
  // `orgTier` decides unlimited, NOT a bare `credit_limit == null` and NOT
  // `subscription.tier_details` (whose absence made `undefined == null` true
  // and rendered a false "∞ / 무제한" that `unavailableReason` was then
  // suppressed from correcting). `=== true` because the field is only
  // meaningful when the tier object exists at all.
  const orgCreditsUnlimited = orgTier != null && orgTier.is_unlimited === true;
  // ⚠ WHICH ROUTE CAN SAY "I DO NOT KNOW" DECIDES THE ORDER HERE (O3).
  //
  // All of these read the SAME COLUMN, `organizations.credits_remaining`, so
  // they cannot disagree about the number. They differ in what they do with a
  // MISSING one, and only one of them is being fixed to say so:
  //   • GET /credits/balance -> returning `null` for "no org row / unknown".
  //   • /admin/credits/org-usage and /api/v1/subscription both serve
  //     `get_org_subscription`, which coerces `row.credits_remaining or 0`
  //     (subscription_service.py:88) -- an unknown pool arrives as a confident
  //     0, which is the O3 defect itself and cannot be detected client-side.
  //
  // So: if the balance route ANSWERED, its answer stands, null included --
  // falling through a null to a route that coerces would turn "unknown" back
  // into "0" the moment that fix lands. org-usage is used only when the
  // balance route did not answer at all, and /api/v1/subscription is not a
  // credit source here any more.
  const orgCreditsRemaining = orgPoolAnswered
    ? orgPoolBalance
    : (orgCredits?.credits_remaining ?? null);
  // membership_tiers, org-scoped. Null when the org has no active plan
  // membership — a free/trial org has no monthly allowance to state (its 5
  // trial credits are a ONE-TIME grant at signup, api/auth.py:351), and a dash
  // is the truth there. It must NOT fall back to subscription_tiers.
  const orgCreditsGranted = orgTier?.credit_limit ?? null;

  // Display subscription tier name and user limits
  const tierDisplayName = subscription?.tier_details?.display_name || seats?.plan_tier || 'Unknown';
  const userLimit = subscription?.tier_details?.user_limit || seats?.max_seats || 999;
  const activeUserCount = users.filter(u => u.is_active).length;
  const isAtUserLimit = userLimit && activeUserCount >= userLimit;

  return (
    <div className="user-mgmt">
      <div className="um-header">
        <div className="um-header-left">
          <h1>User Management</h1>
          <span className={`um-seat-chip ${isAtUserLimit ? 'full' : ''}`}>
            {activeUserCount} / {userLimit === 999 ? '∞' : userLimit} users
            <small style={{ marginLeft: '0.5rem', opacity: 0.8 }}>({tierDisplayName})</small>
          </span>
        </div>
        <button
          className="btn-primary"
          disabled={isAtUserLimit}
          title={isAtUserLimit ? `User limit reached. Upgrade from ${tierDisplayName} tier to add more users.` : 'Invite a new user'}
          onClick={() => {
            setForm({ email: '', full_name: '', role: 'user' });
            setModal({ mode: 'invite' });
          }}
        >+ Invite User</button>
      </div>

      {/* The one org credit panel (T11). It sits under the header rather than in
          it because it is a card, not a chip — `.um-header-left` is a single
          flex row of chips and would squash it. */}
      <div className="um-credit-panel-row" style={{ marginBottom: '1.25rem' }}>
        <OrgCreditPanel
          isKo={isKo}
          remaining={orgCreditsRemaining}
          granted={orgCreditsGranted}
          /* From GET /admin/credits/org-usage, which evaluates
             monthly_credits_for_tier server-side. Null when it could not be
             established, and the panel shows a dash for that -- never a date.
             See the note in App.js. */
          tierGrantsMonthly={orgCredits?.tier_grants_monthly ?? null}
          /* NOT `orgCredits.credits_used`. That column is a LIFETIME counter,
             zeroed only on a tier change (T5), and the old chip printed it as
             "(N used)" beside a monthly allowance — the exact mislabelling T5
             removed from the Settings card. This is the ORG-WIDE per-period
             figure from /profile/membership, the same one the Settings 구독
             card shows, so the two screens state the same usage for the same
             org. The per-USER period figures are in the table below. */
          usedThisPeriod={orgUsedThisPeriod}
          /* ⚠ NOT `orgCredits.period_start` (review 2026-09-24). That field is
             `get_period_start`, whose last resort for an org with no active
             plan membership is the CALENDAR 1st — so feeding it here printed a
             confident 갱신일 for every free/trial org, i.e. a recurring monthly
             grant that will never run. The panel now derives the date only from
             a membership term it can prove, and prints a dash otherwise. Both
             dates are USER-scoped (/profile/membership selects
             `WHERE um.user_id = :user_id`), so an org admin who did not
             personally buy the plan still gets a dash: the fix for that is
             server-side, `next_period_start` on the ORG-scoped payload. */
          planStartDate={planDates?.start_date ?? null}
          planEndDate={planDates?.end_date ?? null}
          unlimited={orgCreditsUnlimited}
          /* KG이니시스 review (T2.7): the usage, not a balance. The English
             half is not in the plan: Copy status: pending_ceo (D9). */
          unavailableReason={orgUsageProbed && orgPoolProbed
            && !orgCreditsUnlimited && orgCreditsRemaining == null
            ? (isKo
              ? '분석 리포트 이용 현황을 불러오지 못했습니다. 권한이 없거나 일시적인 오류일 수 있습니다.'
              : 'Could not load analysis report usage. This may be a permission error or a temporary failure.')
            : null}
        />
      </div>
      
      {isAtUserLimit && (
        <div className="um-limit-warning">
          ⚠️ User limit reached for {tierDisplayName} tier ({activeUserCount}/{userLimit}).{' '}
          <a href="/pricing" target="_blank" rel="noopener noreferrer">Upgrade subscription</a> to add more users.
        </div>
      )}

      {/* See AdminProfileManager: the invite validator answers with an OBJECT
          detail, and an object child unmounts the page. */}
      {error && <div className="um-error">{renderable(error)}<button onClick={() => setError('')}>×</button></div>}
      {message && <div className="um-msg">{message}<button onClick={() => setMessage('')}>×</button></div>}

      <div className="um-toolbar">
        <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}>
          <option value="">All Roles</option>
          <option value="admin">Org-Admin</option>
          {isSiteAdmin && <option value="tester">Sys-Admin</option>}
          <option value="user">Viewer</option>
        </select>
        <input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} maxLength={MAX_LENGTHS.short_text} />
      </div>

      <table className="um-table">
        <thead>
          {/* "Credits" was users.credit_balance — a legacy per-user counter that
              gates nothing on the analyze path, so it could read 0 while the
              user analysed freely. Replaced with real consumption of the SHARED
              org pool this plan month, plus the optional per-user ceiling. */}
          <tr><th>Name</th><th>Email</th><th>Role</th><th>Used this period</th><th>Status</th><th>Last Login</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {filteredUsers.map(u => (
            <tr key={u.id} className={u.is_active ? '' : 'inactive-row'}>
              <td className="name-cell">{u.full_name}</td>
              <td>{u.email}</td>
              <td><span className="role-chip" style={{ background: ROLE_COLORS[u.role] }}>{ROLE_LABELS[u.role] || u.role}</span></td>
              <td className="credit-cell">
                {u.credits_used_period != null ? u.credits_used_period.toLocaleString() : '—'}
              </td>
              <td><span className={`status-chip ${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
              <td>{u.last_login ? new Date(u.last_login).toLocaleString() : '—'}</td>
              <td className="actions-cell">
                <button className="btn-sm" onClick={() => { setForm({ email: u.email, full_name: u.full_name, role: u.role }); setModal({ mode: 'edit', item: u }); }}>Edit</button>
                <button className={`btn-sm ${u.is_active ? 'danger' : 'success'}`} onClick={() => toggleActive(u)}>
                  {u.is_active ? 'Revoke' : 'Activate'}
                </button>
                {u.is_active && <button className="btn-sm" onClick={() => resetPassword(u)}>Reset PW</button>}
              </td>
            </tr>
          ))}
          {filteredUsers.length === 0 && <tr><td colSpan={7} className="empty-cell">No users found</td></tr>}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="um-pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {/* Invite / Edit Modal */}
      {modal && modal.mode !== 'credits' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{modal.mode === 'invite' ? 'Invite User' : 'Edit User'}</h2>
            {modal.mode === 'invite' && <p className="modal-desc">A temporary password will be generated. If email is configured, an invitation will be sent automatically.</p>}
            <div className="modal-form">
              <label>Full Name<input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} maxLength={MAX_LENGTHS.display_name} required /></label>
              <label>Email<input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} maxLength={MAX_LENGTHS.email} required /></label>
              <label>Role
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                  <option value="admin">Org-Admin</option>
                  {isSiteAdmin && <option value="tester">Sys-Admin</option>}
                  <option value="user">Viewer</option>
                </select>
              </label>
              <div className="btn-row">
                <button className="btn-primary" onClick={modal.mode === 'invite' ? handleInvite : handleUpdate}>
                  {modal.mode === 'invite' ? 'Send Invite' : 'Save'}
                </button>
                <button className="btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Temp Password Dialog */}
      {tempPw && (
        <div className="modal-overlay" onClick={() => setTempPw(null)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <h2>Temporary Password</h2>
            <p>Share this password with the user. It won't be shown again.</p>
            <div className="temp-pw">{tempPw}</div>
            <div className="btn-row">
              <button className="btn-primary" onClick={() => { navigator.clipboard.writeText(tempPw); setMessage('Password copied to clipboard'); setTempPw(null); }}>
                📋 Copy & Close
              </button>
              <button className="btn-secondary" onClick={() => setTempPw(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
